/**
 * Account UI Helpers
 *
 * Visual/presentation helpers for account roles.
 * Separated from business logic to keep domain logic free of UI imports.
 *
 * Used by: Sidebar, MobileNav, ProfileButton, ProfileSection — any component
 * needing role-based icons, labels, or colors. (De twee AccountSwitchers die
 * hier ooit stonden zijn in aug 2026 verwijderd; zie accounts/index.ts.)
 *
 * @moved from src/utils/accountUIHelpers.ts (R19 — Fase 2 continuation)
 *
 * F-ACC-CQ-R2-ROLE-COLOR-INCONSISTENCY (fixed Round 3):
 *   This module no longer defines its own color palette. All role colors come
 *   from `designTokens.ROLE_COLORS` (single source of truth). Components keep
 *   importing `ACCOUNT_ROLE_COLORS` from here for backwards compatibility.
 */

import { User, Users, GraduationCap, ShieldCheck } from 'lucide-react';
import { AccountRole } from '../types';
import { ROLE_COLORS } from '../../config/designTokens';

/**
 * Get the icon component for an account role.
 */
export function getAccountIcon(role: AccountRole) {
  switch (role) {
    case 'learner':
      return User;
    case 'parent':
      return Users;
    case 'teacher':
      return GraduationCap;
    case 'admin':
      return ShieldCheck;
  }
}

/**
 * Get the Dutch display label for an account role.
 */
export function getAccountRoleLabel(role: AccountRole): string {
  switch (role) {
    case 'learner':
      return 'Leerling';
    case 'parent':
      return 'Ouder';
    case 'teacher':
      return 'Docent';
    case 'admin':
      return 'Beheerder';
  }
}

/**
 * Color palette for account roles.
 * Re-exported from designTokens.ROLE_COLORS — DO NOT override values here.
 * Components should prefer `ROLE_COLORS` directly; this alias exists for
 * historical call-sites (AccountSwitcher, AccountSwitcherDropdown, etc.).
 */
export const ACCOUNT_ROLE_COLORS: Record<AccountRole, {
  bg: string;
  text: string;
  border: string;
  iconBg: string;
  iconText: string;
  badgeBg: string;
  badgeText: string;
  hoverBorder: string;
}> = {
  learner: ROLE_COLORS.learner,
  parent: ROLE_COLORS.parent,
  teacher: ROLE_COLORS.teacher,
  admin: ROLE_COLORS.admin
};

/**
 * Theme classes for auth and onboarding screens.
 * Centralizes the gradients and borders used across RegisterScreen, OnboardingWizard, etc.
 */
export function getRoleThemeClasses(role: AccountRole) {
  switch (role) {
    case 'learner':
      return {
        background: 'bg-gradient-to-b from-orange-50 via-amber-50/30 to-white',
        gradient: 'from-orange-400 to-amber-400',
        border: 'border-orange-100',
        buttonBg: 'bg-orange-500 hover:bg-orange-600',
        colorScheme: 'amber' as const
      };
    case 'parent':
      return {
        background: 'bg-gradient-to-b from-emerald-50 via-teal-50/30 to-white',
        gradient: 'from-emerald-400 to-teal-400',
        border: 'border-emerald-100',
        buttonBg: 'bg-emerald-600 hover:bg-emerald-700',
        colorScheme: 'emerald' as const
      };
    case 'teacher':
      return {
        background: 'bg-gradient-to-b from-indigo-50 via-blue-50/30 to-white',
        gradient: 'from-indigo-500 to-blue-400',
        border: 'border-indigo-100',
        buttonBg: 'bg-indigo-600 hover:bg-indigo-700',
        colorScheme: 'indigo' as const
      };
    case 'admin':
    default:
      return {
        background: 'bg-gradient-to-b from-slate-50 via-slate-50/30 to-white',
        gradient: 'from-slate-400 to-slate-500',
        border: 'border-slate-200',
        buttonBg: 'bg-slate-600 hover:bg-slate-700',
        colorScheme: 'slate' as const
      };
  }
}